import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, CreditCard, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { SESSION_KEY } from '../helper/common';

const config = {
  labels: {
    title: 'Cashback Redemption',
    subtitle: 'Redeem your reward points for cashback credit to your account.',
    currentPoints: 'Current Reward Points',
    pointsValue: 'Points Value',
    redemptionAmount: 'Redemption Amount',
    //inPoints: 'in Points',
    inCurrency: 'in Rs',
    redeemButton: 'Redeem Now',
    confirmTitle: 'Confirm Redemption',
    confirmMessage: 'Are you sure you want to redeem',
    success: 'Redemption successful!',
    error: 'Redemption failed. Please try again.',
    loading: 'Processing redemption...',
    backToDashboard: 'Back to Dashboard',
    minimumRedemption: 'Minimum redemption is 100 points.',
    maximumRedemption: 'Maximum redemption is your available points.',
    pointsRate: '1 Point = 0.4 Rs'
  },
  apiUrl: process.env.REACT_APP_API_URL_User,
  currency: 'Rs',
  pointsToCurrencyRate: 0.4, //  1 point = 0.4 Rs based on pointsValue calculation
};

const Cashback = () => {
  const [userData, setUserData] = useState(null);
  const [category, setCategory] = useState(null)
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState(null);
  const [redemptionPoints, setRedemptionPoints] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      setUserData(null); // Reset userData on retry/fetch
      const cnic = localStorage.getItem('cnic');
      const cardNo = localStorage.getItem('cardNumber'); // Get cardNo from localStorage set in Dashboard
      if (!cnic) {
        throw new Error('CNIC not found. Please login again.');
      }
      if (!cardNo || cardNo === 'N/A') {
        throw new Error('Card number not found. Please select a card from dashboard.');
      }
      const token = localStorage.getItem(SESSION_KEY);
      const custId = localStorage.getItem('custId');

      // Fetch points data (reuse dashboard endpoint or specific one)
      const response = await axios.get(`${config.apiUrl}/api/user/dashboard`, {
        params: { cnic },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const rawData = response.data;
      if (rawData && rawData.token) {
        localStorage.setItem(SESSION_KEY, rawData.token);
      }

      const mappedData = {
        points: rawData.points || 0,
        pointsValue: rawData.pointsValue || 0,
        custId: rawData.customerId || custId || 'N/A',
        currency: rawData.currency || config.currency,
        cardNo: rawData.cardNo || cardNo, // Use from response or localStorage
      };

      // Additional safety check
      if (!mappedData.cardNo || mappedData.cardNo === 'N/A') {
        throw new Error('Invalid card number retrieved.');
      }

      setUserData(mappedData);
    } catch (err) {
      console.error('Error fetching user data:', err);
      const errorMsg = err.response?.data?.message || err.message || config.labels.error;
      setError(errorMsg);
      toast.error(errorMsg);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePointsChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setRedemptionPoints(value);
    setError(null);
  };

  const handleQuickRedeem = (points) => {
    setRedemptionPoints(points);
    setError(null);
  };

  const validateRedemption = () => {
    if (redemptionPoints < 100) {
      setError(config.labels.minimumRedemption);
      return false;
    }
    if (redemptionPoints > userData.points) {
      setError(config.labels.maximumRedemption);
      return false;
    }
    return true;
  };

  const handleRedeem = async () => {
    if (!validateRedemption()) return;

    setShowConfirm(true);
  };

  const confirmRedeem = async () => {
    try {
      setRedeeming(true);
      setError(null);
      const token = localStorage.getItem(SESSION_KEY);
      const custId = userData.custId;
      const cardNumber = userData.cardNo; // Use directly from userData
      const payload = {
        cardNumber: cardNumber, // Ensure correct mapping
        points: redemptionPoints,
        custId: custId // Ensure correct mapping
      };

      // Correct axios.post usage: (url, data, config)
      const response = await axios.post(`${config.apiUrl}/api/user/redeem`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      // if (response.data.success) {
      //   toast.success(config.labels.success);
      //   // Update local data
      //   setUserData(prev => ({ ...prev, points: prev.points - redemptionPoints }));
      //   setRedemptionPoints(0);
      //   setShowConfirm(false);
      //   // Optionally refetch full data
      //   await fetchUserData();
      // }
      if (response.data.success) {
        toast.success(response.data.message || config.labels.success);
        const oldPoints = userData.points;
        setRedemptionPoints(0);
        setShowConfirm(false);

        // Poll every 1s until server returns updated points (max 8 retries)
        let retries = 0;
        const poll = setInterval(async () => {
          retries++;
          try {
            const cnic = localStorage.getItem('cnic');
            const authToken = localStorage.getItem('authToken');
            const cardNo = localStorage.getItem('cardNumber');
            const custId = localStorage.getItem('custId');


            const res = await axios.get(`${config.apiUrl}/api/user/dashboard`, {
              params: { cnic },
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (res.data && res.data.token) {
              localStorage.setItem(SESSION_KEY, res.data.token);
            }
            if (res.data.points !== oldPoints || retries >= 8) {
              clearInterval(poll);
              setUserData({
                points: res.data.points || 0,
                pointsValue: res.data.pointsValue || 0,
                custId: res.data.customerId || custId || 'N/A',
                currency: res.data.currency || config.currency,
                cardNo: res.data.cardNo || cardNo,
              });
            }
          } catch (e) {
            if (retries >= 8) clearInterval(poll);
          }
        }, 1000);
      } else {
        throw new Error(response.data.message || config.labels.error);
      }
    } catch (err) {
      console.error('Error redeeming points:', err);
      const errorMsg = err.response?.data?.message || err.message || config.labels.error;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setRedeeming(false);
    }
  };

  const getCurrencyValue = () => {
    return (redemptionPoints * config.pointsToCurrencyRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">{config.labels.loading}</div>
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={fetchUserData}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="py-16 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 min-h-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <button
              onClick={() => navigate('/main')}
              className="flex items-center space-x-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-6 mx-auto"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>{config.labels.backToDashboard}</span>
            </button>
            <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4 font-['Inter'] bg-gradient-to-r from-green-600 to-green-700 dark:from-green-400 dark:to-green-500 bg-clip-text text-transparent">
              {config.labels.title}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-lg">
              {config.labels.subtitle}
            </p>
            {/* Display Card No for reference with safety check */}
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Redeeming for Card: {userData.cardNo ? `•••• •••• •••• ${userData.cardNo.slice(-4)}` : 'N/A'}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-slate-200/50 dark:bg-gray-800/80 dark:border-gray-700/50 space-y-8"
          >
            {/* Current Points Display */}
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="text-center p-6 bg-green-50 dark:bg-green-900/20 rounded-xl"
              >
                <Gift className="h-8 w-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{config.labels.currentPoints}</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white">{Number(userData.points).toLocaleString(undefined,)}</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="text-center p-6 bg-blue-50 dark:bg-blue-900/20 rounded-xl"
              >
                <div className="mx-auto mb-2 text-3xl font-bold text-blue-600 dark:text-blue-400">₨</div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{config.labels.pointsValue}</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white">
                  {config.currency} {userData.pointsValue.toLocaleString(undefined)}
                </p>
              </motion.div>
            </div>

            {/* Redemption Form */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {config.labels.redemptionAmount}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={redemptionPoints === 0 ? "" : redemptionPoints}
                  onChange={handlePointsChange}
                  placeholder="Enter points to redeem (min 1,000)"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <span className="absolute right-3 top-3 text-sm text-slate-500 dark:text-slate-400">
                  {config.labels.inPoints}
                </span>
              </div>
              {error && (
                <div className="flex items-center text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {error}
                </div>
              )}
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {config.labels.pointsRate} | Equivalent: {config.currency} {getCurrencyValue()}
              </div>

              {/* Quick Redeem Buttons */}
              <div className="flex flex-wrap gap-2 justify-center">
                {[1000, 2000].map(points => (
                  <motion.button
                    key={points}
                    onClick={() => handleQuickRedeem(points)}
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${redemptionPoints === points
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-200 dark:bg-gray-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-gray-600'
                      }`}
                  >
                    {points.toLocaleString()} {config.labels.inPoints}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Link to Redemption Catalogue */}
            <div className="text-center">
              <p className="text-sm text-slate-600 dark:text-slate-400"> Minimum 1,000 points required for redemption.
              </p>
            </div>

            {/* Redeem Button */}
            <button
              onClick={handleRedeem}
              disabled={redemptionPoints < 1000 || redeeming || redemptionPoints > userData.points}
              className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 dark:from-green-500 dark:to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Gift className="h-5 w-5" />
              <span>{config.labels.redeemButton}</span>
            </button>

            {/* Confirmation Modal */}
            {showConfirm && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                onClick={() => setShowConfirm(false)}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="text-center mb-4">
                    <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400 mx-auto mb-2" />
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{config.labels.confirmTitle}</h3>
                    <p className="text-slate-600 dark:text-slate-400">
                      {config.labels.confirmMessage} {redemptionPoints.toLocaleString()} {config.labels.inPoints} ({config.currency} {getCurrencyValue()})?
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowConfirm(false)}
                      className="flex-1 bg-slate-200 dark:bg-gray-700 text-slate-900 dark:text-white py-2 px-4 rounded-lg hover:bg-slate-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmRedeem}
                      disabled={redeeming}
                      className="flex-1 flex items-center justify-center space-x-2 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      <span>{redeeming ? config.labels.loading : 'Confirm'}</span>
                      {redeeming && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Cashback;